import { useRouterState } from '@tanstack/react-router';
import {
	type PartnerProfile,
	resolvePartner,
} from '@/features/partner-link/config';

export function useAppContext() {
	return useRouterState({
		select: (state) => {
			return state.matches.find((match) => match.context)?.context;
		},
	});
}

// The partner account-linking route (docs/swiggy-account-linking.md): always
// mandatory-login, and — when its utm_source names a known partner — the
// funnel switches to that partner's presentation (see usePartnerLogin).
const PARTNER_LINK_ROUTE_ID = '/ixi-finance/partner/link';

// Routes where login is MANDATORY — the funnel must be non-skippable (no
// skip/close affordance), because the surface is unusable logged-out. `/_auth`
// is the protected layout (covers every route nested under it via `matches`);
// `/travel-credit-card_/apply` is the standalone CBCC application page. Mirrors
// the 2.x `useLogin({ hideSkip: true })` call sites. Marketing surfaces (e.g.
// the CBCC landing apply CTA) are NOT listed — there login stays skippable.
const MANDATORY_LOGIN_ROUTE_IDS: ReadonlySet<string> = new Set([
	'/_auth',
	'/travel-credit-card_/apply',
	PARTNER_LINK_ROUTE_ID,
]);

/**
 * Whether the currently-active route requires login (so the global funnel must
 * hide its skip/close affordance). Reactive to navigation — drives `hideSkip`
 * on the single `<LoginGate>` since the 3.x `login()` takes no per-call options.
 */
export function useMandatoryLogin(): boolean {
	return useRouterState({
		select: (state) =>
			state.matches.some((match) =>
				MANDATORY_LOGIN_ROUTE_IDS.has(match.routeId),
			),
	});
}

/**
 * The active partner profile when the partner account-linking route is matched
 * with a known `utm_source` — drives the funnel's `otpOnly` presentation and
 * its `product` (CMS banner + analytics Page Source) on the single global
 * `<LoginGate>`. Null everywhere else. Referentially stable: profiles come
 * from the registry, so the `useRouterState` selector never loops.
 */
export function usePartnerLogin(): PartnerProfile | null {
	return useRouterState({
		select: (state) => {
			const match = state.matches.find(
				(m) => m.routeId === PARTNER_LINK_ROUTE_ID,
			);
			if (!match) return null;
			const search = match.search as { utm_source?: string } | undefined;
			return resolvePartner(search?.utm_source);
		},
	});
}

// Every ixigo Creators route — landing, legal pages and the `_auth` onboarding
// page (`/ixi-finance/creators`, `/ixi-finance/creators_/…`,
// `/_auth/ixi-finance/creators_/…`).
const CREATORS_ROUTE_ID = /\/ixi-finance\/creators(_|\/|$)/;

/**
 * Whether the login funnel should hide its CMS promo banner (e.g. "Get upto
 * 25% off on flights") — on the creators surfaces, where a booking offer is off
 * message. Drives `hideBanners` on the single global `<LoginGate>`.
 */
export function useHideLoginBanners(): boolean {
	return useRouterState({
		select: (state) =>
			state.matches.some((match) => CREATORS_ROUTE_ID.test(match.routeId)),
	});
}
